import { ISlidingDateRangeTypeOption } from '../../../data/providers/settings/dates/slidingDateRangeProvider';
import {
  IRange,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  ITest,
} from '../../../data/types';

// =============================================================================
// Legacy Settings (V1 format, for migration compatibility)
// =============================================================================

export interface ILegacyScoreboardSettings {
  dateRangeType: ISlidingDateRangeTypeOption;
  dateRange?: IRange;
  testType: ISelectableTestType;
  tags: ISelectableTag[];
  filteredTeams: string[];
  filteredGroups: string[];
  metrics: {
    [testTypeId: string]: ISelectableMetric;
  };
}

// =============================================================================
// Theme Types
// =============================================================================

export type BackgroundType = 'solid' | 'gradient' | 'image';

export type BackgroundPresetId =
  | 'stadium-lights'
  | 'turf-texture'
  | 'concrete-dark'
  | 'gradient-blue'
  | 'gradient-orange'
  | 'abstract-lines';

export interface IBackgroundImage {
  /** Whether this is a built-in preset or custom upload */
  type: 'preset' | 'custom';
  /** Reference to built-in preset (if type is 'preset') */
  presetId?: BackgroundPresetId;
  /** User-uploaded image URL from Firebase Storage (if type is 'custom') */
  customUrl?: string;
  /** Reference to background from the library (if type is 'custom') */
  selectedBackgroundId?: string;
  /** Opacity for overlay effect (0-1) */
  opacity?: number;
  /** Image positioning */
  position?: 'center' | 'top' | 'bottom';
  /** Image sizing */
  size?: 'cover' | 'contain';
}

export type TitleFontSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface IScoreboardTheme {
  /** Main accent color (team color) */
  primaryColor: string;
  /** Secondary accent color */
  secondaryColor: string;
  /** Main background color (or gradient start color) */
  backgroundColor: string;
  /** Secondary background color (for gradient end) */
  backgroundColorSecondary: string;
  /** Stats banner background color */
  bannerBackground: string;
  /** Rank card background color */
  cardBackground: string;
  /** Primary text color */
  textPrimary: string;
  /** Secondary text color */
  textSecondary: string;

  // Typography (Phase 8 - deferred)
  /** Google Font family name */
  fontFamily?: string;
  /** Title font size preset */
  titleFontSize?: TitleFontSize;

  // Background options
  /** Type of background to use */
  backgroundType: BackgroundType;
  /** Background image configuration (if backgroundType is 'image') */
  backgroundImage?: IBackgroundImage;
}

// =============================================================================
// Display Configuration
// =============================================================================

export interface IScoreboardDisplayConfig {
  /** Whether to show athlete positions */
  showPosition: boolean;
  /** Whether to show athlete images */
  showAthleteImage: boolean;
  /** Whether to show the rank badge */
  showRankBadge: boolean;
  /** Selected rank badge icon ID from icon library */
  rankBadgeIconId?: string;
  /** Resolved URL for rank badge icon */
  rankBadgeUrl?: string;
  /** Whether to auto-scroll when content overflows */
  autoScroll: boolean;
  /** Whether to fire confetti on new #1 rank changes */
  enableConfetti: boolean;
}

// =============================================================================
// Branding Configuration
// =============================================================================

export interface IScoreboardBranding {
  /** Custom title (e.g., "2025 NFL COMBINE") */
  customTitle: string;
  /** Custom logo URL (Firebase Storage) */
  logoUrl?: string;
  /** Whether to show the organization logo */
  showOrganizationLogo: boolean;
  /** Selected logo ID from the logo library */
  selectedLogoId?: string;
}

// =============================================================================
// Logo Library Types
// =============================================================================

export interface IScoreboardLogo {
  /** Unique logo ID */
  id: string;
  /** Display name for the logo */
  name: string;
  /** Firebase Storage URL */
  url: string;
  /** Timestamp when uploaded */
  uploadedAt: number;
  /** Whether this logo is active/not deleted */
  active: boolean;
}

// =============================================================================
// Background Library Types
// =============================================================================

export interface IScoreboardBackground {
  /** Unique background ID */
  id: string;
  /** Display name for the background */
  name: string;
  /** Firebase Storage URL */
  url: string;
  /** Timestamp when uploaded */
  uploadedAt: number;
  /** Whether this background is active/not deleted */
  active: boolean;
  /** Default opacity for this background (0-1) */
  defaultOpacity?: number;
}

// =============================================================================
// Icon Library Types
// =============================================================================

export type IconCategory = 'rank' | 'achievement' | 'custom';

export interface IScoreboardIcon {
  /** Unique icon ID */
  id: string;
  /** Display name for the icon */
  name: string;
  /** Firebase Storage URL */
  url: string;
  /** Timestamp when uploaded */
  uploadedAt: number;
  /** Whether this icon is active/not deleted */
  active: boolean;
  /** Category for organization */
  category: IconCategory;
}

// =============================================================================
// Split View Configuration
// =============================================================================

export type SplitColumnFilterType = 'team' | 'group';

export interface ISplitColumnConfig {
  /** Column header title */
  title: string;
  /** Filter by team or group */
  filterType: SplitColumnFilterType;
  /** IDs of teams or groups to include */
  filteredIds: string[];
  /** Per-column tags (overrides global tags for this column in split mode) */
  tags?: ISelectableTag[];
  /** Secondary filter IDs: group IDs when filterType='team', team IDs when filterType='group' */
  secondaryFilterIds?: string[];
}

export interface ISplitViewConfig {
  /** Whether split view is enabled */
  enabled: boolean;
  /** Left column configuration */
  leftColumn: ISplitColumnConfig;
  /** Right column configuration */
  rightColumn: ISplitColumnConfig;
}

// =============================================================================
// View Configuration (Leaderboard vs Force-Time Curve)
// =============================================================================

export type ScoreboardViewMode = 'leaderboard' | 'force-time-curve';

export interface IForceTimeCurveConfig {
  /** Show background grid lines */
  showGrid: boolean;
  /** Highlight peak force point on the curve */
  showPeakForce: boolean;
  /** Use theme colors or custom color for the curve */
  colorScheme: 'theme' | 'custom';
  /** Custom curve color (if colorScheme is 'custom') */
  customColor?: string;
}

export interface IScoreboardViewConfig {
  /** Which views are enabled for this scoreboard instance */
  availableViews: ScoreboardViewMode[];
  /** Initial view mode on load */
  defaultView: ScoreboardViewMode;
  /** Force-time curve display options */
  forceTimeCurve: IForceTimeCurveConfig;
}

// =============================================================================
// Scoreboard Settings (current format)
// =============================================================================

export interface IScoreboardSettings extends ILegacyScoreboardSettings {
  /** Settings version identifier (2 = current format) */
  version: 2;
  /** Theme configuration */
  theme: IScoreboardTheme;
  /** Display configuration */
  display: IScoreboardDisplayConfig;
  /** Branding configuration */
  branding: IScoreboardBranding;
  /** Split view configuration */
  splitView: ISplitViewConfig;
  /** View mode configuration */
  viewConfig: IScoreboardViewConfig;
}

// =============================================================================
// Template Types
// =============================================================================

export interface IScoreboardTemplate {
  /** Unique template ID */
  id: string;
  /** Template display name */
  name: string;
  /** Template description */
  description: string;
  /** Whether template is active (not deleted) */
  active: boolean;
  /** The saved settings */
  settings: IScoreboardSettings;
  /** Timestamp when template was created */
  createdAt: number;
  /** Timestamp when template was last updated */
  updatedAt: number;
  /** Whether template is shared with organization */
  shared?: boolean;
  /** Whether template was created by Hawkin (presets) */
  hawkinCreated?: boolean;
}

// =============================================================================
// Provider State Types
// =============================================================================

export interface IScoreboardSettingsState {
  /** Currently selected/active template (null if using defaults) */
  selected: IScoreboardTemplate | null;
  /** All available templates for the user */
  templates: IScoreboardTemplate[];
  /** Current settings (from selected template or defaults) */
  settings: IScoreboardSettings;
}

// =============================================================================
// Animation & Real-time Types
// =============================================================================

export interface IRankChange {
  /** Athlete ID */
  athleteId: string;
  /** Previous rank (null if new entry) */
  previousRank: number | null;
  /** Current rank */
  currentRank: number;
  /** Direction of change */
  direction: 'up' | 'down' | 'new' | 'same';
}

export interface IRecordEvent {
  /** The test that set the record */
  test: ITest;
  /** The metric that was broken */
  metricKey: string;
  /** Previous record value */
  previousValue: number;
  /** New record value */
  newValue: number;
  /** Timestamp of the record */
  timestamp: number;
}

// =============================================================================
// Force-Time Curve Types
// =============================================================================

export interface IForceTimeCurveData {
  /** Time values in milliseconds */
  time: number[];
  /** Combined force values in Newtons */
  force: number[];
  /** Left plate force values (if available) */
  forceLeft?: number[];
  /** Right plate force values (if available) */
  forceRight?: number[];
  /** Peak force value */
  peakForce: number;
  /** Index of peak force in the arrays */
  peakForceIndex: number;
  /** Sampling rate in Hz */
  hertz: number;
  /** Test ID this data belongs to */
  testId: string;
}

// =============================================================================
// Default Values
// =============================================================================

export const DEFAULT_THEME: IScoreboardTheme = {
  primaryColor: '#1a73e8',
  secondaryColor: '#4285f4',
  backgroundColor: '#121212',
  backgroundColorSecondary: '#1a1a2e',
  bannerBackground: '#1e1e1e',
  cardBackground: 'rgba(0, 0, 0, 0.85)',
  textPrimary: '#ffffff',
  textSecondary: '#b0b0b0',
  backgroundType: 'solid',
};

export const DEFAULT_DISPLAY_CONFIG: IScoreboardDisplayConfig = {
  showPosition: true,
  showAthleteImage: true,
  showRankBadge: true,
  autoScroll: false,
  enableConfetti: false,
};

export const DEFAULT_BRANDING: IScoreboardBranding = {
  customTitle: 'Scoreboard',
  showOrganizationLogo: true,
};

export const DEFAULT_SPLIT_VIEW_CONFIG: ISplitViewConfig = {
  enabled: false,
  leftColumn: {
    title: 'Team A',
    filterType: 'team',
    filteredIds: [],
    tags: [],
    secondaryFilterIds: [],
  },
  rightColumn: {
    title: 'Team B',
    filterType: 'team',
    filteredIds: [],
    tags: [],
    secondaryFilterIds: [],
  },
};

export const DEFAULT_FORCE_TIME_CURVE_CONFIG: IForceTimeCurveConfig = {
  showGrid: true,
  showPeakForce: true,
  colorScheme: 'theme',
};

export const DEFAULT_VIEW_CONFIG: IScoreboardViewConfig = {
  availableViews: ['leaderboard'],
  defaultView: 'leaderboard',
  forceTimeCurve: DEFAULT_FORCE_TIME_CURVE_CONFIG,
};

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Type guard to check if settings are in the current format (has version: 2)
 */
export const isCurrentSettingsFormat = (
  settings: ILegacyScoreboardSettings | IScoreboardSettings
): settings is IScoreboardSettings => {
  return 'version' in settings && settings.version === 2;
};

// =============================================================================
// Migration Utilities
// =============================================================================

/**
 * Migrate legacy settings to current format with default values for new fields
 */
export const migrateLegacySettings = (
  legacySettings: ILegacyScoreboardSettings
): IScoreboardSettings => {
  return {
    ...legacySettings,
    version: 2,
    theme: DEFAULT_THEME,
    display: DEFAULT_DISPLAY_CONFIG,
    branding: DEFAULT_BRANDING,
    splitView: DEFAULT_SPLIT_VIEW_CONFIG,
    viewConfig: DEFAULT_VIEW_CONFIG,
  };
};
